// src/lib/mso-vocabulary.ts
//
// ★★ ANNEX A 22 — the closed vocabulary of the MSO rule engine, as the OFFICER
// CONSOLE needs it: the values a selector may offer, the words an officer reads for
// each, and the two money conversions the screens share.
//
// ⚠ WHY THIS IS A MIRROR AND NOT AN IMPORT. The authoritative arrays live in
// `packages/rules/src/mso/vocabulary.ts`, but that package's entry point also exports
// the definition hash, which reads `node:crypto`; this bundle holds no server package
// (`server-only-imports.test.ts`). So the handful of constants a selector needs are
// repeated here, and `mso-vocabulary.test.ts` READS the server file and fails if either
// copy changes without the other. The server still refuses an unknown value: these
// arrays decide what a control OFFERS, never what is accepted.

import { formatIsoDate } from './format'

export const MSO_BENEFICIARY_TYPES = ['PRINCIPAL', 'DEPENDANT'] as const
export const MSO_CARE_SETTINGS = ['OUTPATIENT', 'INPATIENT'] as const
export const MSO_VISIT_TYPES = ['FIRST_CONSULTATION', 'FOLLOW_UP'] as const
export const MSO_HSP_TYPES = ['SPECIALIST', 'POLYCLINIC', 'HOSPITAL'] as const
export const MSO_CAP_STATUSES = ['REACHED', 'NOT_REACHED'] as const
export const MSO_BILL_INDICATORS = ['NORMAL', 'MEDISAVE'] as const
export const MSO_SPECIAL_CONDITIONS = ['DETERMINATION_OF_FITNESS', 'SERVICE_INJURY', 'NONE'] as const
export const MSO_COMPONENTS = ['BILL_AMOUNT', 'FIRST_CONSULTATION_FEE', 'HSP_FEES', 'WARD_CHARGES'] as const
export const MSO_ROUNDING_MODES = ['HALF_UP', 'HALF_EVEN', 'FLOOR'] as const
export const MSO_OPERATORS = ['eq', 'neq', 'in', 'present', 'absent'] as const
export const MSO_CALCULATION_REFUSALS = [
  'INVALID_INPUT',
  'INCOMPLETE_INPUT',
  'CONFLICTING_INPUT',
  'ENTITLEMENT_NOT_COVERING',
  'NO_RULE_IN_FORCE',
  'AMBIGUOUS_RULE',
  'UNDEFINED_SCENARIO',
] as const

/** The scalar fields a condition may test; an array is the closed set of values, a string names a free type. */
export const MSO_CONDITION_FIELDS: Readonly<Record<string, readonly string[] | 'boolean' | 'text'>> = {
  beneficiaryType: MSO_BENEFICIARY_TYPES,
  careSetting: MSO_CARE_SETTINGS,
  visitType: MSO_VISIT_TYPES,
  governmentReferral: 'boolean',
  hspType: MSO_HSP_TYPES,
  capStatus: MSO_CAP_STATUSES,
  billIndicator: MSO_BILL_INDICATORS,
  specialBillCondition: MSO_SPECIAL_CONDITIONS,
  eligibleWardClass: 'text',
  billedWardClass: 'text',
  voluntaryWardUpgrade: 'boolean',
}
export const MSO_CONDITION_FIELD_NAMES: readonly string[] = Object.keys(MSO_CONDITION_FIELDS)

/** What an officer reads for a machine token. Anything not listed is shown as it arrived, never hidden. */
export const MSO_FIELD_LABELS: Readonly<Record<string, string>> = {
  beneficiaryType: 'Beneficiary type',
  careSetting: 'Care setting',
  visitType: 'Visit type',
  governmentReferral: 'Government referral',
  hspType: 'HSP type',
  capStatus: 'Annual cap status',
  billIndicator: 'Bill indicator',
  specialBillCondition: 'Special bill condition',
  eligibleWardClass: 'Eligible ward class',
  billedWardClass: 'Billed ward class',
  voluntaryWardUpgrade: 'Voluntary ward upgrade',
}

export const MSO_COMPONENT_LABELS: Readonly<Record<string, string>> = {
  BILL_AMOUNT: 'Bill amount',
  FIRST_CONSULTATION_FEE: 'First consultation fee',
  HSP_FEES: 'HSP fees',
  WARD_CHARGES: 'Ward charges',
  '*': 'Every component',
}

export const MSO_OPERATOR_LABELS: Readonly<Record<string, string>> = {
  eq: 'is',
  neq: 'is not',
  in: 'is one of',
  present: 'is supplied',
  absent: 'is not supplied',
}

export const MSO_ROUNDING_LABELS: Readonly<Record<string, string>> = {
  HALF_UP: 'Half up',
  HALF_EVEN: 'Half even',
  FLOOR: 'Round down',
}

export const MSO_REFUSAL_LABELS: Readonly<Record<string, string>> = {
  INVALID_INPUT: 'Invalid input',
  INCOMPLETE_INPUT: 'Incomplete input',
  CONFLICTING_INPUT: 'Conflicting input',
  ENTITLEMENT_NOT_COVERING: 'Entitlement does not cover the date',
  NO_RULE_IN_FORCE: 'No rule in force',
  AMBIGUOUS_RULE: 'Ambiguous rule',
  UNDEFINED_SCENARIO: 'Scenario not defined',
}

/** "FIRST_CONSULTATION" becomes "First consultation" when no better label is registered. */
export function humaniseToken(token: string): string {
  const spaced = token.toLowerCase().replace(/_/g, ' ')
  if (spaced.length === 0) return token
  // Acronyms keep their case: "MSO_CALCULATED" is "MSO calculated", never "Mso calculated".
  return (spaced.charAt(0).toUpperCase() + spaced.slice(1)).replace(/\b(mso|hsp)\b/gi, (w) => w.toUpperCase())
}

const SUMMARY_WORDS: Readonly<Record<string, string>> = {
  HALF_UP: 'half up',
  HALF_EVEN: 'half even',
  FLOOR: 'down',
  NOT_REACHED: 'not reached',
  REACHED: 'reached',
}

/**
 * The engine's own sentence, with its machine tokens read as words: "BILL_AMOUNT: 15% of $135.20, rounded HALF_UP"
 * becomes "Bill amount: 15% of $135.20, rounded half up". The UI review of 2026-10-14 found a PSD officer reading
 * BILL_AMOUNT and NOT_REACHED in the trace. Only ALL-CAPS tokens with an underscore, and the five single words above,
 * are touched; every figure and every other word is exactly as the engine wrote it.
 */
export function plainSummary(summary: string): string {
  return summary
    .replace(/\b(?:[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+|FLOOR|REACHED)\b/g, (token) => {
      const component = MSO_COMPONENT_LABELS[token]
      if (component !== undefined) return component
      return SUMMARY_WORDS[token] ?? humaniseToken(token).toLowerCase()
    })
    // ★ ONE money style and ONE date style (UAT re-run F-21, F-32). The engine writes "15520 cents", "$20.28" and
    // "2026-10-27"; an officer reads "SGD 155.20", "SGD 20.28" and "27 Oct 2026". Only the form changes: the figure
    // is the engine's own, and a whole number of cents is converted by moving the decimal point, never by arithmetic.
    .replace(/\b(\d+) cents?\b/g, (_whole, cents: string) => `SGD ${centsToDollars(cents)}`)
    .replace(/\$(\d[\d,]*\.\d{2,})/g, 'SGD $1')
    .replace(/\b(\d{4}-\d{2}-\d{2})\b/g, (_whole, iso: string) => formatIsoDate(iso))
}

/** "15520" -> "155.20", "5" -> "0.05", by string only. Thousands separators are added for 4+ figure amounts. */
export function centsToDollars(cents: string): string {
  const digits = cents.replace(/^0+(?=\d)/, '').padStart(3, '0')
  const whole = digits.slice(0, -2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return `${whole}.${digits.slice(-2)}`
}

/**
 * The engine's exact share, a figure in cents with four decimal places ("2328.5000"), as dollars ("SGD 23.285").
 * Trailing zeros are dropped but never below two decimal places, so a whole-cent figure still reads "SGD 23.28".
 */
export function exactCentsAsMoney(exact: string): string {
  const match = /^([+-]?)(\d+)(?:\.(\d+))?$/.exec(exact.trim())
  if (match === null) return exact
  const [, sign = '', whole = '0', fraction = ''] = match
  const digits = whole.padStart(3, '0')
  const dollars = digits.slice(0, -2)
  const cents = digits.slice(-2) + fraction
  return `${sign}SGD ${dollars}.${cents.replace(/0+$/, '').padEnd(2, '0')}`
}

export function msoRefusalLabel(kind: string): string {
  return MSO_REFUSAL_LABELS[kind] ?? humaniseToken(kind)
}

/**
 * What an officer does next after a refusal (UAT re-run F-30). The engine says what is wrong; this says what to do about
 * it, in the officer's words. Where the refusal is about the officer's own entries the answer is to correct them; where
 * it is about the rules, nothing the officer types here will change it, and the sentence says who can.
 */
export function msoRefusalNextStep(kind: string): string {
  switch (kind) {
    case 'INVALID_INPUT':
    case 'INCOMPLETE_INPUT':
    case 'CONFLICTING_INPUT':
      return 'Correct the fields named below and calculate again. Nothing was priced.'
    case 'ENTITLEMENT_NOT_COVERING':
      return 'Check the entitlement dates against the treatment date and calculate again. Nothing was priced.'
    case 'AMBIGUOUS_RULE':
      return 'Two rules claim this bill and neither outranks the other. Ask a PSD administrator to retire one of them, or change its precedence, then calculate again. Nothing was priced.'
    case 'NO_RULE_IN_FORCE':
    case 'UNDEFINED_SCENARIO':
      return 'Check the treatment date and the details of the bill and calculate again. If they are right, no rule covers this case: ask a PSD administrator to review the MSO rules. Nothing was priced.'
    default:
      return 'Check what you entered and calculate again. If it keeps being refused, contact PRS support. Nothing was priced.'
  }
}

/**
 * Basis points as an exact percentage, by integer arithmetic only: 1500 becomes "15.00%".
 * (`bp / 100` would be a float on a money surface; `WardRulesPage` makes the same argument.)
 */
export function basisPointsLabel(basisPoints: number): string {
  const whole = Math.trunc(basisPoints / 100)
  const fraction = Math.abs(basisPoints % 100)
  return `${whole}.${String(fraction).padStart(2, '0')}%`
}

/**
 * What an officer typed in a percentage box, as whole basis points, or the reason it cannot be.
 *
 * ⚠ String arithmetic, not `Number(x) * 100`: `Number('15.1') * 100` is `1510.0000000000002`.
 * A third decimal place is REFUSED rather than rounded, for the reason `parseCents` gives:
 * a rounded rate is a different rate. Whether a rate may be 0 or 100 is the server's
 * business; only the SHAPE is checked here.
 */
export function percentToBasisPoints(raw: string): { basisPoints: number } | { error: string } {
  const trimmed = raw.trim().replace(/%$/, '')
  if (trimmed.length === 0) return { error: 'Enter the officer share as a percentage, for example 15 or 15.50.' }
  if (/^-/.test(trimmed)) return { error: 'A share cannot be below 0 percent.' }
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(trimmed)) {
    return {
      error:
        'Enter the officer share as a percentage with at most two decimal places, for example 15 or 15.50. ' +
        'A longer figure is refused rather than rounded.',
    }
  }
  const [whole = '0', fraction = ''] = trimmed.split('.')
  const basisPoints = Number(whole) * 100 + Number(fraction.padEnd(2, '0'))
  if (basisPoints > 10_000) return { error: 'A share cannot be above 100 percent.' }
  return { basisPoints }
}

/**
 * A cent amount typed as dollars (`135.20`), or the reason it cannot be sent.
 *
 * ⚠ The conversion splits the DECIMAL STRING: `Number('135.20') * 100` is a float on a money
 * path and the route refuses `"155.20"` and `155.2` outright (INVALID_INPUT), so a figure
 * that reaches the server as anything but whole cents never leaves this screen. More than
 * two decimals is refused, never rounded.
 */
export function dollarsToCents(raw: string): { cents: number } | { error: string } {
  const trimmed = raw.trim()
  if (trimmed.length === 0) return { error: 'Enter an amount in dollars and cents, for example 135.20.' }
  // ★ F-18: a negative amount gets its own sentence. It used to be told about decimal places, which it does not have.
  if (/^[-\u2212]\s*\d/.test(trimmed)) {
    return { error: 'An amount cannot be negative. Enter the amount of the bill as a positive figure, for example 135.20.' }
  }
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
    return {
      error:
        'Enter an amount in dollars and cents, for example 135.20. More than two decimal places is refused ' +
        'rather than rounded.',
    }
  }
  const [dollars = '0', fraction = ''] = trimmed.split('.')
  const cents = Number(dollars) * 100 + Number(fraction.padEnd(2, '0'))
  if (!Number.isSafeInteger(cents)) return { error: 'That amount is too large to send exactly.' }
  return { cents }
}

/** Whole cents as the dollar string a form field shows: 13520 becomes "135.20". Integer arithmetic only. */
export function centsToDollarsText(cents: number): string {
  const whole = Math.trunc(cents / 100)
  const fraction = Math.abs(cents % 100)
  return `${whole}.${String(fraction).padStart(2, '0')}`
}
